import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { hasLearningEvent, trackLearningEvent } from '../../lib/learningEvents';
import ResourceMediaPanel from '../../components/education/ResourceMediaPanel';

const ResourcePage = () => {
  const { slug } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const matterId = searchParams.get('matterId');
  const questionKey = searchParams.get('questionKey');
  const topicId = searchParams.get('topicId');
  const [resource, setResource] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completionSaving, setCompletionSaving] = useState(false);
  const [articleCompleted, setArticleCompleted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResource = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('education_resources')
          .select('*')
          .eq('slug', slug)
          .eq('is_active', true)
          .single();

        if (error || !data) throw new Error("Resource not found.");
        setResource(data);

        const { data: mediaData, error: mediaError } = await supabase
          .from('education_resource_media')
          .select('*')
          .eq('resource_id', data.id)
          .eq('publication_status', 'published')
          .order('display_priority', { ascending: true })
          .order('created_at', { ascending: true });

        if (mediaError) {
          console.warn('Unable to load resource media:', mediaError);
          setMediaItems([]);
        } else {
          setMediaItems(mediaData || []);
        }

        if (profile?.id) {
          const alreadyCompleted = await hasLearningEvent({
            userId: profile.id,
            matterId,
            topicId,
            resourceId: data.id,
            eventType: 'article_completed'
          });
          setArticleCompleted(alreadyCompleted);

          await trackLearningEvent({
            userId: profile.id,
            matterId,
            questionKey,
            topicId,
            resourceId: data.id,
            eventType: 'resource_opened',
            metadata: {
              source: 'resource_page',
              resource_slug: data.slug
            }
          });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchResource();
  }, [slug, profile?.id, matterId, questionKey, topicId]);

  const markArticleComplete = async () => {
    if (!profile?.id || !resource?.id || completionSaving || articleCompleted) return;

    try {
      setCompletionSaving(true);
      await trackLearningEvent({
        userId: profile.id,
        matterId,
        questionKey,
        topicId,
        resourceId: resource.id,
        eventType: 'article_completed',
        metadata: {
          source: 'resource_page',
          resource_slug: resource.slug
        }
      });
      setArticleCompleted(true);
    } catch (err) {
      console.warn('Unable to mark article complete:', err);
    } finally {
      setCompletionSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-10 w-10 text-slate-300" /></div>;

  if (error) return (
    <div className="max-w-xl mx-auto mt-20 text-center p-10 bg-white rounded-xl shadow-sm border border-slate-200">
      <SafeIcon icon={FiIcons.FiSearch} className="h-12 w-12 text-slate-300 mx-auto mb-4" />
      <h2 className="text-xl font-bold text-slate-900">Resource not found</h2>
      <button onClick={() => navigate(-1)} className="mt-6 text-blue-600 font-bold uppercase tracking-widest text-xs">Go Back</button>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-20">
      <button onClick={() => navigate(-1)} className="flex items-center text-slate-400 hover:text-slate-600 transition-colors">
        <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-2" /> Back
      </button>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-8 md:p-12 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-6">
            <span className="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-bold uppercase tracking-widest rounded-full">{resource.resource_type}</span>
            <span className="text-slate-300">•</span>
            <span className="text-slate-500 text-xs font-medium flex items-center"><SafeIcon icon={FiIcons.FiClock} className="mr-1.5" /> {resource.estimated_minutes} min read</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight mb-4">{resource.title}</h1>
          <p className="text-lg text-slate-500 font-medium italic">{resource.short_description}</p>
        </div>

        <ResourceMediaPanel resource={resource} mediaItems={mediaItems} />

        <div className="p-8 md:p-12 prose prose-slate max-w-none">
          <div className="whitespace-pre-wrap text-slate-700 leading-relaxed space-y-4">
            {resource.content_markdown}
          </div>
          {resource.resource_type === 'article' && (
            <div className="not-prose mt-8 border-t border-slate-100 pt-6">
              <button
                type="button"
                onClick={markArticleComplete}
                disabled={completionSaving || articleCompleted}
                className="inline-flex items-center rounded-md bg-green-50 px-4 py-2 text-xs font-bold uppercase tracking-widest text-green-700 transition-colors hover:bg-green-100 disabled:opacity-60"
              >
                {completionSaving ? (
                  <SafeIcon icon={FiIcons.FiLoader} className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <SafeIcon icon={articleCompleted ? FiIcons.FiCheckCircle : FiIcons.FiCheck} className="mr-2 h-4 w-4" />
                )}
                {articleCompleted ? 'Article completed' : 'Mark article complete'}
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="bg-slate-50 rounded-xl p-8 border border-slate-200 text-center">
        <h3 className="font-bold text-slate-900 mb-2">Have questions about this topic?</h3>
        <p className="text-sm text-slate-500 mb-6">Your financial advisor can help clarify how these concepts apply to your specific situation.</p>
        <button onClick={() => navigate(-1)} className="px-6 py-2 bg-slate-800 text-white text-xs font-bold uppercase tracking-widest rounded-lg hover:bg-slate-900 transition-colors shadow-sm">
          Return to Summary
        </button>
      </div>
    </div>
  );
};

export default ResourcePage;
